import {Reserve} from "../common/types/Types";
import {EntitySelect} from "../common/inputs/EntitySelect";
import {BYTable} from "../common/views/BYTable";
import {CommonHelper} from "../common/CommonHelper";
import React, {useContext, useRef, useState} from "react";
import ReserveBadge from "./ReserveBadge";
import {BYStatusBadge} from "../common/inputs/BYStatusButtonGroup";
import {BYInput} from "../common/inputs/BYInput";
import BYChangeStatusButtons from "../common/buttons/BYChangeStatusButtons";
import {useAlert} from "react-alert";
import {useThrottledLayoutEffect, useUpdateReservesStatus} from "../api/hooks";
import {AuthContext} from "../providers/AuthProvider";
import BYExportButtons from "../common/buttons/BYExportButtons";

interface ReserveListProps {
    data: Reserve[];
    loading?: boolean;
}

const ReserveList = (props: ReserveListProps) => {
    const {data, loading} = props;
    const {formatter} = useContext(AuthContext);

    const operationId = data.length ? data[0].lot?.operationRefOperation : undefined;

    const [searchText, setSearchText] = useState('');
    const [filteredReserves, setFilteredReserves] = useState<Reserve[]>(data);
    const [selectedReserves, setSelectedReserves] = useState<Reserve[]>([]);
    const [selectedBuildings, setSelectedBuildings] = useState<string[]>([]);
    const [status, setStatus] = useState();
    const [selectedPhases, setSelectedPhases] = useState<number[]>([]);
    const alert = useAlert();
    const {mutateAsync: mutateReservesStatus} = useUpdateReservesStatus();
    const tableRef = useRef(null);

    useThrottledLayoutEffect(() => {
        const newReserves = data.filter((reserve) => {
            const code = `${reserve.lot?.numeroLot} ${reserve.libelle} ${CommonHelper.formatAcquereur(reserve.lot?.acquereur)} ${reserve.entreprise?.nom} / ${reserve.corpsEtat?.libCorpsEtat}`;
            const searchTextUpperCased = searchText.toUpperCase();
            const hasBuilding = !selectedBuildings.length || selectedBuildings.includes(reserve.lot?.batiment);
            const hasPhase = !selectedPhases.length || selectedPhases.includes(reserve.phaseRefPhase);
            const hasStatus = !status || (reserve.statutRefStatut === status && !reserve.indicRas);

            return CommonHelper.includesWithoutAccents(searchTextUpperCased, code) && hasBuilding && hasPhase && hasStatus;
        });

        setFilteredReserves(newReserves);
    }, 300, [searchText, status, selectedBuildings, selectedPhases, data]);

    const selectedReservesHaveRas = selectedReserves.some(r => r.indicRas === true);

    return <>

        <div className="row mb-3">
            <div className="col-3">
                <BYInput
                    type="text"
                    title="Rechercher"
                    placeholder="Numéro de lot, libellé, etc."
                    className="form-control"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
            </div>
            <div className="col-3">
                <EntitySelect type="buildings"
                              isMulti
                              title="Bâtiment"
                              placeholder="Tous les Bâtiment"
                              operationId={operationId} onChange={(value) => setSelectedBuildings(value)}
                              value={selectedBuildings}/>
            </div>
            <div className="col-3">
                <EntitySelect type="status" title="Statut" onChange={(value) => setStatus(value)}
                              value={status}/>
            </div>
            <div className="col-3">
                <EntitySelect type="phase" title="Phase" isMulti={true} onChange={(value) => setSelectedPhases(value)}
                              placeholder="Toutes les Phases"
                              value={selectedPhases}/>
            </div>
        </div>

        <BYTable
            className="center-content"
            loading={loading}
            data={filteredReserves}
            withSelect
            mRef={tableRef}
            columns={['lotRefLot', 'lot', 'coordonneeY', 'coordonneeX', 'numBadge', 'photos', 'aPourLocalisations', 'aPourSituations', 'libelle', 'corpsEtatEntrepriseRefCorpsEtatEntreprise', 'statut', 'phase', 'dateSaisi', 'dateLevee']}
            columnNames={['Lot', 'Bat', 'Etg', 'Client', 'Num', 'Photo', 'Localisation', 'Situation', 'Libellé réserve', 'Entreprise', 'Statut', 'Phase', 'Saisie le', 'Levée le']}
            dataTransform={{
                'lotRefLot': (reserve) => reserve.lot?.numeroLot,
                'lot': (reserve) => reserve.lot?.batiment,
                'coordonneeY': (reserve) => reserve.lot?.etage,
                'coordonneeX': (reserve) => <div className="text-left">{CommonHelper.formatAcquereur(reserve.lot?.acquereur)}</div>,
                'numBadge': (reserve) => <ReserveBadge reserve={reserve}/>,
                'aPourLocalisations': (reserve) => formatter.getLocalisationsDescription(reserve.aPourLocalisations.map(ap => ap.localisationRefLocalisation)),
                'aPourSituations': (reserve) => formatter.getSituationsDescription(reserve.aPourSituations.map(ap => ap.situationRefSituation)),
                'statut': (reserve) => !reserve.indicRas ? <BYStatusBadge statusId={reserve.statutRefStatut}
                                                                          statusText={formatter.getStatusDescription(reserve.statutRefStatut)}/> : '',
                'libelle': (reserve) => <div className="text-left">{reserve.libelle}</div>,
                'phase': (reserve) => formatter.getPhaseDescription(reserve.phaseRefPhase),
                'corpsEtatEntrepriseRefCorpsEtatEntreprise': (reserve) => reserve.entreprise ? `${reserve.entreprise?.nom} / ${reserve.corpsEtat?.libCorpsEtat}` : '',
                'photos': (reserve) => reserve.photos.length ? <i className="fa fa-camera fa-xl"/> : '',
                'dateSaisi': (reserve) => CommonHelper.formatDate(reserve.dateSaisi),
                'dateLevee': (reserve) => CommonHelper.formatDate(reserve.dateLevee),
            }}
            rowClassName={(reserve) => {
                return reserve.indicSupprime ? 'table-danger' : '';
            }}
            onSortEquality={(a, b) => {
                return  a.numBadge -  b.numBadge;
            }}
            sortDataTransform={{
                'dateSaisi': (reserve) => CommonHelper.dateStringToTime(reserve.dateSaisi),
                'dateLevee': (reserve) => CommonHelper.dateStringToTime(reserve.dateLevee),
                'statut': (reserve) => !reserve.indicRas ? formatter.getStatusDescription(reserve.statutRefStatut) : '',
                'photos': (reserve) => reserve.photos.length
            }}
            onClick={(reserve) => {
                window.open(`/reserve/${reserve.refReserve}`);
            }}
            height={60}
            keyExtractor={(r) => `reserve-list-${r.refReserve}`}
            onRowSelect={(reserves) => setSelectedReserves(reserves)}
            leftCustomView={<>
                <p className="mr-2">Exporter</p>
                <BYExportButtons selectedReserves={selectedReserves} selectedPhases={selectedPhases} type="reserve" />
            </>}
            rightCustomView={<>
                <p className="mr-2">Statut {selectedReserves.length ? `(${selectedReserves.length})` : ''}</p>
                <BYChangeStatusButtons
                    disabled={!selectedReserves.length || selectedReservesHaveRas}
                    onChange={async (status) => {
                        mutateReservesStatus({
                            reserveIds: selectedReserves.map(r => r.refReserve),
                            statusId: status.refStatut
                        }).then(() => {
                            tableRef.current?.clearSelection();
                            alert.info(`${selectedReserves.length} réserves mis en statut ${status.libStatut}`);
                        })
                    }}/>
            </>}
        />

    </>;
};

export default ReserveList;
